<template>
	<div
		v-if="editing"
		class="role-control role-edit sgnl-flex sgnl-align-center sgnl-gap-8"
	>
		<v-select
			:model-value="member.Role || 'Member'"
			:items="roleOptions"
			density="compact"
			hide-details
			:menu-icon="''"
			:loading="loading"
			class="role-select"
			@update:model-value="$emit('select', $event)"
		>
			<template #append-inner>
				<ChevronDown16 class="select-chevron" />
			</template>
		</v-select>
		<v-btn
			icon
			variant="text"
			size="small"
			class="role-cancel-btn"
			@click="$emit('cancel')"
		>
			<Close16 class="sgnl-icon-color-error" />
			<v-tooltip
				activator="parent"
				location="bottom"
				>Cancel</v-tooltip
			>
		</v-btn>
	</div>
	<div
		v-else
		class="role-control role-display sgnl-flex sgnl-align-center sgnl-gap-8"
	>
		<span
			class="role-label sgnl-flex sgnl-align-center sgnl-gap-4 sgnl-paragraph-s-regular sgnl-text-color-secondary"
		>
			<Enterprise16
				v-if="(member.Role || 'Member') === 'Owner'"
				class="role-label-icon sgnl-icon-color-secondary sgnl-flex-shrink-0"
			/>
			<User16
				v-else
				class="role-label-icon sgnl-icon-color-secondary sgnl-flex-shrink-0"
			/>
			{{ member.Role || "Member" }}
		</span>
		<v-btn
			v-if="canEdit"
			icon
			variant="text"
			size="small"
			class="role-edit-btn"
			@click="$emit('start-edit')"
		>
			<Edit16 class="sgnl-icon-color-secondary" />
			<v-tooltip
				activator="parent"
				location="bottom"
				>Edit role</v-tooltip
			>
		</v-btn>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import Edit16 from "@carbon/icons-vue/es/edit/16";
import Enterprise16 from "@carbon/icons-vue/es/enterprise/16";
import User16 from "@carbon/icons-vue/es/user/16";
import { OrganizationRole, OrganizationUserFormatted } from "@/types/api/organizations";

/**
 * Role cell for the organization members table: a read-only label with an
 * Owner/Member icon plus a hover-revealed edit pencil, which swaps to a role
 * dropdown and a cancel button while editing. Used by both the desktop cell
 * and the mobile card.
 */
export default defineComponent({
	name: "MemberRoleControl",
	components: { Close16, ChevronDown16, Edit16, Enterprise16, User16 },
	props: {
		member: { type: Object as PropType<OrganizationUserFormatted>, required: true },
		editing: { type: Boolean, default: false },
		roleOptions: { type: Array as PropType<OrganizationRole[]>, required: true },
		loading: { type: Boolean, default: false },
		canEdit: { type: Boolean, default: false },
	},
	emits: {
		"start-edit": () => true,
		cancel: () => true,
		select: (_role: OrganizationRole) => true,
	},
});
</script>

<style lang="css" scoped>
.role-control {
	flex: 1;
	min-width: 0;
}

.role-select {
	max-width: 100%;
	min-width: 120px;
	flex: 1;
}

.role-label-icon {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
}

/* Edit affordance is revealed on row hover, like the copy button in RecentlyCompletedTable. */
.role-edit-btn {
	opacity: 0;
	transition: opacity var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
}

.sgnl-data-table tbody tr:hover .role-edit-btn,
.role-edit-btn:focus-visible {
	opacity: 1;
}

@media (max-width: 719px) {
	/* No hover on touch — keep the role edit affordance always visible in cards. */
	.role-edit-btn {
		opacity: 1;
	}
}
</style>
